import classNames from "classnames";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../../components/buttons/button";
import { InfoCard } from "../../../components/cards/infoCard";
import { EmptyState } from "../../../components/empty";
import { EventCard } from "../../../components/event/eventCard";
import { EventVisibilityToggle } from "../../../components/event/eventVisibility";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useCreateEventMutation,
  useEditEventMutation,
} from "../../../redux/features/event/event.api";
import {
  CreateEvent,
  EventStatus,
} from "../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../redux/hooks";
import {
  formatDate,
  formatToCurrency,
  getCurrencyShort,
  getEventCurrencyFromTickets,
} from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";
import { PreviewStep } from "../preview/previewStep";

export const EventPreview = ({
  finalPayload,
  currentPayload,
  setFinalPayload,
  setStep,
  mode,
  id = "",
  showLivePreview,
  setShowLivePreview,
}: {
  finalPayload: CreateEvent;
  currentPayload: CreateEvent;
  setFinalPayload: React.Dispatch<React.SetStateAction<CreateEvent>>;
  setStep: (step: number) => void;
  mode: "create" | "edit";
  id?: string;
  showLivePreview: boolean;
  setShowLivePreview: (status: boolean) => void;
}) => {
  const [ticketsPurchase, setTicketsPurchase] = useState<TicketPurchase[]>([]);
  const [eventCurrency, setEventCurrency] = useState<PayoutCurrency>();

  const navigate = useNavigate();

  const { profile, hasPayoutAccount } = useAppSelector((state) => state.auth);

  const [createEvent, { isLoading: creatingEvent }] = useCreateEventMutation();
  const [draftEvent, { isLoading: draftingEvent }] = useCreateEventMutation();
  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation();

  useEffect(() => {
    if (finalPayload.tickets.length > 0) {
      setEventCurrency(getEventCurrencyFromTickets(finalPayload.tickets));
    }
    // eslint-disable-next-line
  }, [finalPayload.tickets]);

  return showLivePreview ? (
    <div className="relative flex flex-col gap-3">
      <InfoCard
        leftIcon="info"
        title="This is an event preview"
        className="!sticky !top-0 !z-10 !border-[2px] !border-purple-200 !bg-purple-100 !shadow-md"
        rightElement={
          <button
            onClick={() => {
              setShowLivePreview(false);
            }}
            type="button"
            className="w-fit font-bold underline"
          >
            Go back
          </button>
        }
      />

      <PreviewStep
        event={{
          createdBy: {
            _id: profile?._id ?? "",
            email: profile?.email ?? "",
            password: profile?.password ?? "",
            firstName: profile?.firstName ?? "",
            lastName: profile?.lastName ?? "",
            phoneNumber: profile?.phoneNumber ?? "",
            userType: profile?.userType ?? "organiser",
            status: profile?.status ?? "active",
            createdAt: profile?.createdAt ?? "",
            updatedAt: profile?.updatedAt ?? "",
            isOrganisation: !!profile?.isOrganisation,
            organisation: profile?.organisation,
          },
          currency: "NGN",
          isTrending: false,
          discounts: [],
          ticketCount: 0,
          userType: "organiser",
          _id: "",
          createdAt: "",
          updatedAt: "",
          slug: "",
          __v: 0,
          ...finalPayload,
        }}
        tickets={finalPayload.tickets.map((t, idx) => {
          return {
            ...t,
            eventId: "",
            __v: 0,
            _id: `${idx}`,
            createdAt: "",
            updatedAt: "",
            description: t.description ?? "",
            quantity: t.quantityAvailable,
          };
        })}
        ticketsPurchase={ticketsPurchase}
        setTicketsPurchase={setTicketsPurchase}
        canBuy={false}
        canShare={false}
        setStep={() => {}}
        refetchTickets={() => {}}
      />
    </div>
  ) : (
    <>
      <div className="flex w-full flex-col gap-[12px]">
        <EventCard
          image={finalPayload.image}
          name={finalPayload.name}
          location={finalPayload.location}
          startDate={formatDate(
            finalPayload.startDate,
            "ddd, MMMM DD, YYYY",
            true,
          )}
          endDate={formatDate(finalPayload.endDate, "ddd, MMMM DD, YYYY", true)}
          startTime={finalPayload.startTime}
          endTime={finalPayload.endTime}
          noTickets={finalPayload.tickets.length}
          totalGuests={0}
          isOrganizer={true}
        />

        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2">
          <Button
            type="button"
            variation="white"
            className="!rounded-2xl"
            onClick={() => {
              setShowLivePreview(true);
              // window.open(`/events/${id}/preview`, "_blank");
            }}
            text="Preview Event"
            icon="show-eye"
          />

          <Button
            type="button"
            variation="white"
            className="!rounded-2xl"
            onClick={() => {
              setStep(1);
            }}
            text="Edit Event"
            icon="pencil"
          />
        </div>

        {finalPayload.tickets.length === 0 && (
          <EmptyState
            text="No Tickets Created"
            subtext="When you add tickets they would show up here."
            subtextClassName="!max-w-[340px]"
            image="/assets/images/ticket-glass.png"
          />
        )}

        {finalPayload.tickets.map((ticket, idx) => {
          return (
            <BgWrapper
              className="flex items-center gap-4"
              key={`ticket-${idx}-preview`}
            >
              <div className="flex-1 text-sm text-grey-900">
                <p className="text-base font-bold">{ticket.name}</p>
                <p className="capitalize">
                  {ticket.ticketType === "free"
                    ? "Free"
                    : formatToCurrency(ticket.price!, {
                        currency: getCurrencyShort(eventCurrency),
                      })}
                </p>
                <p className="capitalize">
                  {ticket.quantityType === "unlimited"
                    ? "Unlimited"
                    : `0/${ticket.quantityAvailable}`}
                </p>
              </div>
            </BgWrapper>
          );
        })}

        <EventVisibilityToggle
          visibility={finalPayload.visibility}
          onChange={(vis) => {
            setFinalPayload({
              ...finalPayload,
              visibility: vis,
            });
          }}
        />

        <BgWrapper
          className={classNames("grid items-center gap-2.5", {
            "grid-cols-2": mode === "create",
            "grid-cols-1": mode !== "create",
          })}
        >
          {mode === "create" && (
            <Button
              text="Save as Draft"
              type="button"
              onClick={async () => {
                const payloadData = {
                  ...finalPayload,
                  startDate: formatDate(finalPayload.startDate) ?? "",
                  endDate: formatDate(finalPayload.endDate) ?? "",
                  status: "draft" as EventStatus,
                };

                if (mode === "create") {
                  const resp = await draftEvent(payloadData);

                  if (resp.data?.status === "success") {
                    toast.success("Saved as draft");
                    navigate("/events?EventStatus=draft", { replace: true });
                  }
                }
              }}
              variation="plain"
              className=""
              loading={draftingEvent}
              disabled={draftingEvent}
            />
          )}

          <Button
            text="Publish Event"
            type="button"
            onClick={async () => {
              const payloadData = {
                ...finalPayload,
                startDate: formatDate(finalPayload.startDate) ?? "",
                endDate: formatDate(finalPayload.endDate) ?? "",
                status: "upcoming" as EventStatus,
              };

              if (payloadData.tickets.length === 0) {
                toast.error(
                  "Please add at least one ticket before you can publish your event",
                );
                return;
              }

              if (
                mode === "create" &&
                (!eventCurrency || hasPayoutAccount[eventCurrency])
              ) {
                const resp = await createEvent(payloadData);

                if (resp.data?.status === "success") {
                  navigate(
                    `/events/${encodeURIComponent(resp.data.data.event.slug)}/published?eventName=${encodeURIComponent(resp.data.data.event.name)}`,
                    {
                      replace: true,
                    },
                  );
                }
              } else if (
                mode === "create" &&
                !!eventCurrency &&
                !hasPayoutAccount[eventCurrency]
              ) {
                const resp = await createEvent({
                  ...payloadData,
                  status: "draft",
                });

                if (resp.data?.status === "success") {
                  navigate(
                    `/events/${resp.data!.data.event._id}/add-payout/${eventCurrency}`,
                    {
                      replace: true,
                    },
                  );
                }
              } else if (
                mode === "edit" &&
                (!eventCurrency || hasPayoutAccount[eventCurrency])
              ) {
                const resp = await editEvent({ id: id, payload: payloadData });

                if (resp.data?.status === "success") {
                  navigate(
                    `/events/${encodeURIComponent(resp.data.data.event.slug)}/published?eventName=${encodeURIComponent(resp.data.data.event.name)}`,
                    {
                      replace: true,
                    },
                  );
                }
              } else if (
                mode === "edit" &&
                !!eventCurrency &&
                !hasPayoutAccount[eventCurrency]
              ) {
                const resp = await editEvent({
                  id: id,
                  payload: { ...payloadData, status: "draft" },
                });

                if (resp.data?.status === "success") {
                  navigate(
                    `/events/${resp.data!.data.event._id}/add-payout/${eventCurrency}`,
                    {
                      replace: true,
                    },
                  );
                }
              }
            }}
            loading={creatingEvent || editingEvent}
            disabled={creatingEvent || editingEvent}
          />
        </BgWrapper>
      </div>
    </>
  );
};
